// PortalHandoffScreen — the /portal-handoff?t=<token> landing.
//
// v0.51.0 — the InSpec CLIENT PORTAL's "DropPin" button arrives here with a
// short-lived signed hand-off token (see api/portal-handoff). We POST the token,
// the API verifies it + creates-or-matches the Contact by email and mints the
// SAME signed droppin_contact session a magic-link invite mints — then we land on
// /my-items. The user is signed in as themselves (name/email), never anonymous.
//
// v0.x (2026-07-20, Brett) — ONE flat dark screen (no white card floating on
// dark), a PROMINENT DropPin logo, and the REAL server error surfaced: a
// host-level failure returns a NON-JSON body, so we read the raw response text
// (not just "HTTP 500") so a hand-off failure is actually diagnosable on screen.

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { theme } from '../theme';
import { setContactSession, clearContactSession } from '../utils/contact-session';
import DropPinWordmark from '../components/DropPinWordmark';

export default function PortalHandoffScreen() {
  const [params] = useSearchParams();
  const token = params.get('t') || params.get('token') || '';
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // 'loading' | 'ok' | 'error'
  const [err, setErr] = useState(null);
  const [who, setWho] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!token) throw new Error('Missing hand-off token. Go back to the portal and open DropPin again.');
        const r = await fetch('/api/portal-handoff', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token }),
        });
        if (!r.ok) {
          // Surface the REAL error, not a bare "HTTP 500". A host-level crash
          // returns a NON-JSON body, so read the raw text first, THEN try to
          // parse the { error, details } shape our API normally returns.
          const raw = await r.text().catch(() => '');
          let detail = '';
          try { const b = JSON.parse(raw); detail = (b && (b.details || b.error)) || ''; }
          catch (_e) { detail = (raw || '').replace(/\s+/g, ' ').trim().slice(0, 300); }
          throw new Error(`HTTP ${r.status}${detail ? ' — ' + detail : ''}`);
        }
        const payload = await r.json();
        if (cancelled) return;
        setContactSession(payload);
        setWho(payload?.contact?.name || payload?.contact?.email || null);
        setStatus('ok');
        // v0.x (2026-07-05) — TARGET SCREEN: the API echoes a resolved in-app
        // route; whitelist it so an unexpected value can't redirect off-app.
        const SAFE_ROUTES = ['/my-items', '/projects'];
        const dest = SAFE_ROUTES.includes(payload?.route) ? payload.route : '/my-items';
        setTimeout(() => navigate(dest, { replace: true }), 600);
      } catch (e) {
        if (cancelled) return;
        // SECURITY — a FAILED hand-off must not leave a prior CACHED identity in
        // place: the SPA reads droppin.contact.v1 on the next screen, so without
        // clearing it the user could walk into DropPin as whoever was signed in
        // before (the server also clears the httpOnly cookie on error).
        clearContactSession();
        setErr(e?.message || String(e));
        setStatus('error');
      }
    })();
    return () => { cancelled = true; };
  }, [token, navigate]);

  const sub = { fontSize: 13, color: 'rgba(255,255,255,0.65)' };
  const head = { fontSize: 20, margin: '0 0 8px', color: 'white', fontWeight: 700 };

  return (
    <div style={{
      minHeight: '100vh', background: '#0f172a', color: 'white',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    }}>
      <div style={{ width: 420, maxWidth: '100%', textAlign: 'center' }}>
        {/* Prominent DropPin logo on every state (the wordmark is em-scaled, so a
            larger font-size enlarges the whole mark + pin). */}
        <div style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.01em', marginBottom: 22, color: 'white' }}>
          <DropPinWordmark />
        </div>
        {status === 'loading' && (
          <>
            <h1 style={head}>Signing you in…</h1>
            <div style={sub}>Bringing you across from your client portal.</div>
          </>
        )}
        {status === 'ok' && (
          <>
            <h1 style={head}>{who ? `Signed in as ${who}` : "You're in."}</h1>
            <div style={sub}>Loading your items…</div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 11, color: theme.danger, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>Hand-off failed</div>
            <h1 style={{ ...head, fontSize: 19 }}>We couldn't sign you in</h1>
            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.72)', marginBottom: 18, wordBreak: 'break-word', lineHeight: 1.5 }}>
              {err || 'The link may have expired. Go back to the portal and open DropPin again.'}
            </div>
            {/* SECURITY — deliberately NO "Go to DropPin" button here. It linked
                to /my-items, walking the user into the app on whatever session the
                browser still held — so a failed hand-off could land a client as a
                previous (internal, all-locations) user. A client's only valid entry
                is a fresh hand-off from their InSpec client portal, so we direct
                them back there rather than into the app. */}
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)' }}>
              Return to your client portal and open DropPin again to retry.
            </div>
          </>
        )}
      </div>
    </div>
  );
}
